import React from 'react'
import { createRoot, hydrateRoot } from 'react-dom/client'
import { createInertiaApp } from '@inertiajs/react'
import { resolvePageComponent } from 'laravel-vite-plugin/inertia-helpers'
import '@v5/styles/globals.css'

/**
 * v5 Inertia client entry.
 *
 * Mounts on `<main id="root">` (set up by inertia-v5.blade.php) rather than
 * the @inertia-rendered `<div id="app" data-page=...>`. This mirrors v4's
 * pattern: data-page can't reliably hold the full scorecard payload, so we
 * stash it on `window.pageData` and pass it through `initialPage` instead.
 *
 * Lean by design: no MUI, no Emotion, no easy-peasy, no AppLayout.
 */

const rootElement = document.getElementById('root')

// Inertia's bootstrap reads `dataset.page` even when we pass `initialPage`.
// Stub a minimal value so it doesn't NPE.
if (rootElement && !rootElement.dataset.page) {
	rootElement.dataset.page = JSON.stringify({})
}

const initApp = async () => {
	const data = window.pageData?.props?.data || {}
	if (data.isBuilder === true) return

	const queryParams = new URLSearchParams(window.location.search)
	const params = new URLSearchParams({
		referrer: document.referrer,
		url: window.location.href,
	})

	const splitTestId = data.split_test?.id
	if (splitTestId) {
		params.set('splitTestId', splitTestId)
	}

	const fbadId = queryParams.get('fbad_id')
	if (fbadId) {
		params.set('fbad_id', fbadId)
	}

	const saCid = queryParams.get('sa_cid')
	if (saCid) {
		params.set('sa_cid', saCid)
	}

	const csrfToken =
		window.initialData.tokens?.csrf ||
		document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') ||
		''

	try {
		const response = await fetch(`${window.location.origin}${data.base_url || ''}/init?${params}`, {
			headers: {
				'X-Requested-With': 'XMLHttpRequest',
				'X-CSRF-TOKEN': csrfToken,
			},
		})
		if (!response.ok) {
			throw new Error(`Init request failed with status ${response.status}`)
		}
		const { tokens } = await response.json()
		window.initialData.tokens = tokens
		data.tokens = tokens
	} catch (error) {
		// eslint-disable-next-line no-console
		console.error(error)
	}
}

const mountApp = () => {
	const pageUrl = new URL(window.location.href)
	pageUrl.searchParams.delete('rk')
	pageUrl.searchParams.delete('sa_cid')
	window.pageData.url = `${pageUrl.pathname}${pageUrl.search || ''}${pageUrl.hash || ''}`

	createInertiaApp({
		id: 'root',
		page: window.pageData,
		resolve: name =>
			resolvePageComponent(
				`/resources/react/v5/components/pages/${name}/${name}.jsx`,
				import.meta.glob('/resources/react/v5/components/pages/*/*.jsx'),
			),
		setup: ({ el, App, props }) => {
			const ssr = window.pageData?.ssr === true
			if (ssr) {
				hydrateRoot(el, <App {...props} initialPage={window.pageData} />)
			} else {
				createRoot(el).render(<App {...props} initialPage={window.pageData} />)
			}
		},
		progress: {
			color: 'hsl(var(--primary))',
		},
	})
}

initApp().finally(mountApp)
